// FILE: src/components/admin_pages/LoginAdminLottery.js

import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Button,
    Divider,
    Paper,
    Checkbox,
    FormControlLabel,
    Link,
} from '@mui/material';
import { GoogleLogin } from "@react-oauth/google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import api from "../axios/axiosInstance";
import ModalPrivacy from '../../mainpagecomponents/Legal/ModalPrivacy';
import ModalTOS from '../../mainpagecomponents/Legal/ModalTOS';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    width: '90%',
    maxWidth: 600,
};

const LoginAdminLottery = ({ onLogin, onClose }) => {
    const [error, setError] = useState('');
    const [agreeAll, setAgreeAll] = useState(false);
    const [openTerms, setOpenTerms] = useState(false);
    const [openPrivacy, setOpenPrivacy] = useState(false);
    const [microsoftLoading, setMicrosoftLoading] = useState(false);
    const navigate = useNavigate();

    // Authorized emails
    const ALLOWED_EMAILS = [
        "nizankhan@gmail.com",
        "dostumkhan@outlook.com",
        "nizankhan@hotmail.com",
        "khandostum@gmail.com"
    ];

    const handleGoogleSuccess = async (credentialResponse) => {
        console.log("🚀 Lottery Admin Google login started");
        const decoded = jwtDecode(credentialResponse.credential);
        console.log("📧 Decoded Google user:", decoded.email);

        // Check if email is authorized
        if (!ALLOWED_EMAILS.includes(decoded.email)) {
            console.error("❌ Unauthorized email attempted lottery admin access:", decoded.email);
            setError(`Access denied. Your email (${decoded.email}) is not authorized for lottery management access.`);
            return;
        }

        console.log("✅ Email authorized for lottery admin access");
        try {
            const response = await api.post(
                `/auth/social/login/`,
                {
                    provider: "google",
                    id_token: credentialResponse.credential,
                }
            );
            console.log("✅ Backend response:", response.data);

            const { user } = response.data;

            if (typeof onLogin === "function") {
                console.log("🟢 Calling onLogin with user:", user);
                await onLogin(user);
            }

            if (onClose) onClose();

            console.log("🟢 Navigating to lottery management...");
            setTimeout(() => {
                navigate("/lottery-management");
            }, 200);
        } catch (err) {
            console.error("❌ Lottery Admin Google login failed:", err);
            setError("Google login failed. Please try again.");
        }
    };

    const handleGoogleError = () => {
        setError("Google login failed.");
    };

    const handleMicrosoftLogin = () => {
        if (microsoftLoading) return;

        setMicrosoftLoading(true);
        setError('');

        try {
            console.log("🟡 Using simple OAuth flow for lottery admin");

            const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
            const redirectUri = `${window.location.origin}/social-callback`;
            const scope = encodeURIComponent("openid email profile");
            const state = encodeURIComponent(JSON.stringify({
                timestamp: Date.now(),
                returnUrl: "/lottery-management",
                isAdmin: true,
                isLottery: true
            }));

            // Use the standard OAuth URL
            const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
                `client_id=${clientId}&` +
                `response_type=code&` +
                `redirect_uri=${encodeURIComponent(redirectUri)}&` +
                `response_mode=query&` +
                `scope=${scope}&` +
                `state=${state}&` +
                `prompt=select_account`;

            console.log("🟡 Generated Lottery Admin Auth URL:", authUrl);
            console.log("🟡 Will redirect to /lottery-management after login");
            window.location.href = authUrl;

        } catch (error) {
            console.error("🔴 Microsoft lottery admin login failed:", error);
            setError(`Microsoft login failed: ${error.message}`);
            setMicrosoftLoading(false);
        }
    };

    return (
        <>
            <Dialog open onClose={onClose} fullWidth maxWidth="xs">
                <DialogContent
                    sx={{
                        p: 0,
                        bgcolor: "background.default",
                        borderRadius: 3,
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            zIndex: 1,
                            color: "primary.main",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Paper elevation={0} sx={{ p: 4, pt: 5, textAlign: "center", bgcolor: "transparent" }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, color: "text.primary" }}>
                            Lottery Admin Sign In
                        </Typography>

                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                            Access the lottery management dashboard
                        </Typography>

                        {error && (
                            <Typography color="error" sx={{ mb: 2 }}>
                                {error}
                            </Typography>
                        )}

                        <Box sx={{ textAlign: "left", mb: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={agreeAll}
                                        onChange={(e) => setAgreeAll(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        I agree to the{' '}
                                        <Link component="button" onClick={() => setOpenTerms(true)}>
                                            Terms of Service
                                        </Link>{' '}
                                        and{' '}
                                        <Link component="button" onClick={() => setOpenPrivacy(true)}>
                                            Privacy Policy
                                        </Link>
                                    </Typography>
                                }
                            />
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "center", mb: 2, pointerEvents: agreeAll ? "auto" : "none", opacity: agreeAll ? 1 : 0.5 }}>
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={handleGoogleError}
                                useOneTap={false}
                            />
                        </Box>

                        <Divider sx={{ my: 2, color: "#ccc" }}>or</Divider>

                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={microsoftLoading ? null : <MicrosoftIcon />}
                            onClick={handleMicrosoftLogin}
                            color="primary"
                            disabled={!agreeAll || microsoftLoading}
                        >
                            {microsoftLoading ? "Redirecting..." : "Sign in with Microsoft"}
                        </Button>

                        <Typography variant="caption" sx={{ mt: 3, display: "block", color: "text.secondary" }}>
                            Admin access only. Restricted to authorized emails.
                        </Typography>
                    </Paper>
                </DialogContent>
            </Dialog>

            {/* Terms Modal */}
            <ModalTOS open={openTerms} onClose={() => setOpenTerms(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Terms of Service
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {/* Insert terms content here */}
                    </Typography>
                </Box>
            </ModalTOS>

            {/* Privacy Modal */}
            <ModalPrivacy open={openPrivacy} onClose={() => setOpenPrivacy(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {/* Insert privacy content here */}
                    </Typography>
                </Box>
            </ModalPrivacy>
        </>
    );
};

export default LoginAdminLottery;
